// Thin wrapper around @telegram-apps/sdk-react helpers so the rest of the app
// imports from one place. Falls back to no-ops when run outside Telegram (dev).

import {
  hapticFeedback,
  mainButton,
  backButton,
  miniApp,
  viewport,
} from '@telegram-apps/sdk-react';

export function haptic(kind: 'selection' | 'light' | 'success' | 'error' = 'selection') {
  try {
    switch (kind) {
      case 'selection':
        if (hapticFeedback.selectionChanged.isAvailable()) hapticFeedback.selectionChanged();
        break;
      case 'light':
        if (hapticFeedback.impactOccurred.isAvailable()) hapticFeedback.impactOccurred('light');
        break;
      case 'success':
        if (hapticFeedback.notificationOccurred.isAvailable())
          hapticFeedback.notificationOccurred('success');
        break;
      case 'error':
        if (hapticFeedback.notificationOccurred.isAvailable())
          hapticFeedback.notificationOccurred('error');
        break;
    }
  } catch {
    // ignore — happens only outside Telegram
  }
}

export function showBackButton(onClick: () => void): () => void {
  if (!backButton.isMounted()) return () => {};
  backButton.show();
  const off = backButton.onClick(onClick);
  return () => {
    off();
    backButton.hide();
  };
}

export function hideBackButton() {
  if (backButton.isMounted()) backButton.hide();
}

export function setMainButton(params: {
  text: string;
  onClick: () => void;
  loading?: boolean;
  visible?: boolean;
}): () => void {
  if (!mainButton.isMounted()) return () => {};
  mainButton.setParams({
    text: params.text,
    isEnabled: true,
    isLoaderVisible: !!params.loading,
    isVisible: params.visible !== false,
  });
  const off = mainButton.onClick(params.onClick);
  return () => {
    off();
    mainButton.setParams({ isVisible: false });
  };
}

export function closeMiniApp() {
  try {
    if (miniApp.close.isAvailable()) miniApp.close();
  } catch {
    // noop
  }
}

export function openBotChat(botUsername: string) {
  const url = `https://t.me/${botUsername}`;
  window.open(url, '_blank');
  setTimeout(closeMiniApp, 300);
}

export function readSafeInsets() {
  try {
    const insets = viewport.contentSafeAreaInsets();
    return insets;
  } catch {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
}

export function getInitData(): string {
  // In dev, prefer a signed payload from VITE_MOCK_INIT_DATA so the backend
  // HMAC check actually passes. Generate one with
  //   go run ./cmd/tools/signinitdata
  // and drop it into web/miniapp/.env.local.
  if (import.meta.env.DEV) {
    const mock = import.meta.env.VITE_MOCK_INIT_DATA as string | undefined;
    if (mock) return mock;
  }

  // window.Telegram.WebApp is provided by telegram-web-app.js in index.html
  // (or stubbed by mockEnv.ts in dev when no VITE_MOCK_INIT_DATA is set).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tg = (window as any).Telegram?.WebApp;
  return tg?.initData ?? '';
}
